import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  DuckDBStatusEnum,
  selectDuckDBStatus,
  setScanDataTableExists,
} from '../duckDBSlice';
import useDuckQueryScanData from './useDuckQueryScanData';

/**
 * useTableExists - A React hook that checks if a table exists in DuckDB.
 *
 * This hook will run a DuckDB query to check if a specified table exists,
 * but only when the DuckDBStatus changes to 'dataLoaded'. The hook resets
 * its internal state to allow for re-running the query when DuckDBStatus changes.
 *
 * @param {string} tableName - The name of the table to check for.
 */
const useTableExists = (tableName: string) => {
  const dispatch = useAppDispatch();
  const duckDBStatus = useAppSelector(selectDuckDBStatus);
  const [hasChecked, setHasChecked] = useState(false);

  // Skip query if DuckDB is not loaded or if it's already been checked
  const skipQuery = duckDBStatus !== DuckDBStatusEnum.dataLoaded || hasChecked;
  const tables = useDuckQueryScanData<TableQueryResult>(
    'SHOW TABLES;',
    skipQuery
  );

  useEffect(() => {
    // Reset the state if duckDBStatus changes
    if (duckDBStatus !== DuckDBStatusEnum.dataLoaded) {
      setHasChecked(false);
      dispatch(setScanDataTableExists(false));
    } else if (!hasChecked && tables.data) {
      // Run the query only once when duckDBStatus changes to 'dataLoaded'
      dispatch(
        setScanDataTableExists(tables.data.some((el) => el.name === tableName))
      );
      setHasChecked(true);
    }
  }, [duckDBStatus, hasChecked, tables]);
};

interface TableQueryResult {
  name: string;
}

export default useTableExists;
